import { Button, Card, Space, Typography } from "antd";
import { StatusTag } from "@/views/components/StatusTag";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useDetailBack } from "@/utils/hooks/useDetailBack";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";
import type { AiCorpusPublishRow } from "./AiCorpusPublishPage";

export interface AiCorpusPublishDetailPageProps {
    loadDetail?: (id: string) => Promise<AiCorpusPublishRow>;
    publish?: (id: number) => Promise<unknown>;
    remove?: (id: number) => Promise<unknown>;
    revert?: (id: number) => Promise<unknown>;
}

/** 复用 Vue AI语料发布详情接口。 */
export async function loadAiCorpusPublishDetail(id: string): Promise<AiCorpusPublishRow> {
    const response = await request.service.get<AiCorpusPublishRow>(`/corpus-publish/vo/${id}`);
    return response.data;
}

/** 详情页状态动作保持与旧 Vue 服务方法相同的接口契约。 */
async function publishAiCorpusDetail(id: number): Promise<unknown> {
    return (await request.service.put(`/corpus-publish/publish/${id}`)).data;
}

async function deleteAiCorpusDetail(id: number): Promise<unknown> {
    return (await request.service.delete(`/corpus-publish/${id}`)).data;
}

async function revertAiCorpusDetail(id: number): Promise<unknown> {
    return (await request.service.put(`/corpus-publish/revert/${id}`)).data;
}

function isDraft(detail: AiCorpusPublishRow): boolean {
    return Number(detail.corpusPublish.state) === 1;
}

function formatDate(value?: string, includeTime = false): string {
    if (!value) return DEFAULT_VALUE;
    return value.replace("T", includeTime ? " " : "").slice(0, includeTime ? 19 : 10);
}

/** AI语料发布详情，保留 Vue 的只读字段、状态操作和发布数量边界。 */
export function AiCorpusPublishDetailPage({
    loadDetail = loadAiCorpusPublishDetail,
    publish = publishAiCorpusDetail,
    remove = deleteAiCorpusDetail,
    revert = revertAiCorpusDetail
}: AiCorpusPublishDetailPageProps) {
    const navigate = useNavigate();
    const goBack = useDetailBack("/intelligentAssistant/aiCorpus/publish");
    const banButton = useUserStore(state => state.banButton);
    const { id = "" } = useParams<{ id: string }>();
    const [detail, setDetail] = useState<AiCorpusPublishRow>({ corpusPublish: { id: "", state: 1 }, corpusList: [] });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (!disposed) setDetail(result);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("AI语料发布详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    const runPublish = async () => {
        if (detail.corpusList.length < 10) {
            setWarningMessage("至少发布10条语料");
            return;
        }
        if (!(await confirmDialog("是否发布，确认后将发布在企业微信的AI语料收集？"))) return;
        await publish(Number(detail.corpusPublish.id));
        notifyOperationSuccess("已发布");
        setWarningMessage(null);
    };

    const runAction = async (
        action: (id: number) => Promise<unknown>,
        confirmMessage: string,
        successMessage: string
    ) => {
        if (!(await confirmDialog(confirmMessage))) return;
        await action(Number(detail.corpusPublish.id));
        notifyOperationSuccess(successMessage);
    };

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="ai-corpus-publish-detail-page">
            <Card>
                <Typography.Title level={4}>AI语料发布详情</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {warningMessage ? (
                    <Alert className="mb-4" message={warningMessage} role="alert" type="warning" />
                ) : null}
                <Space className="mb-4" orientation="vertical" size="small">
                    <span>
                        状态：<StatusTag>{isDraft(detail) ? "草稿" : "已发布"}</StatusTag>
                    </span>
                    <span>创建时间：{formatDate(detail.corpusPublish.createdTime, true)}</span>
                    <span>日期：{formatDate(detail.corpusPublish.updatedTime)}</span>
                </Space>
                <Typography.Title level={5}>语料内容</Typography.Title>
                <ZhGrid gap={[20, 20]} cols={4} style={{ gridTemplateColumns: "repeat(4, minmax(280px, 1fr))" }}>
                    {detail.corpusList.map((corpus, index) => (
                        <div key={String(corpus.id ?? index)}>
                            <Typography.Text type="secondary">{`语料${index + 1}：`}</Typography.Text>
                            <span>{(corpus as { content?: string }).content || DEFAULT_VALUE}</span>
                        </div>
                    ))}
                </ZhGrid>
                <Space className="mt-6">
                    {isDraft(detail) ? (
                        <>
                            <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                                <Button
                                    aria-label={`发布 ${detail.corpusPublish.id}`}
                                    disabled={banButton}
                                    type="primary"
                                    onClick={() => void runPublish()}
                                >
                                    发布
                                </Button>
                            </Permission>
                            <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                                <Button
                                    aria-label={`编辑 ${detail.corpusPublish.id}`}
                                    disabled={banButton}
                                    onClick={() =>
                                        void navigate(
                                            `/intelligentAssistant/aiCorpus/publish/edit/${detail.corpusPublish.id}`
                                        )
                                    }
                                >
                                    编辑
                                </Button>
                            </Permission>
                            <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                                <Button
                                    aria-label={`删除 ${detail.corpusPublish.id}`}
                                    disabled={banButton}
                                    onClick={() => void runAction(remove, "是否删除，确认后将不再展示", "已删除")}
                                >
                                    删除
                                </Button>
                            </Permission>
                        </>
                    ) : (
                        <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                            <Button
                                aria-label={`撤回 ${detail.corpusPublish.id}`}
                                disabled={banButton}
                                type="primary"
                                onClick={() =>
                                    void runAction(
                                        revert,
                                        "是否撤回，确认后将撤回此日期在企业微信发布的AI语料收集",
                                        "已撤回"
                                    )
                                }
                            >
                                撤回
                            </Button>
                        </Permission>
                    )}
                    <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                        <Button aria-label="返回" disabled={banButton} onClick={goBack}>
                            返回
                        </Button>
                    </Permission>
                </Space>
            </Card>
        </section>
    );
}
